import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';
import { GlobalEventBusService, GroupContextService, hd, IGroupModel, TicketService } from '@nx-fe/services';
import { AttachmentService, IAttachmentServiceConfig } from '../services/attachmentService';
import { Activity, ActivityTypeEnum, NoteActivity, Ticket } from '../../ticket.model';
import { GlobalEvents } from 'sspapp/core/global-event-constants';
import { TicketContextService } from '../../services/ticket-context.service';

@Component({
  selector: 'ssp-ticket-header',
  templateUrl: './ticket-header.component.html',
  styleUrls: ['./ticket-header.component.scss'],
})
export class TicketHeaderComponent implements OnInit, OnChanges {
  @Input('isShrink')
  isShrink: boolean = false;

  @Input('ticket')
  ticket: Ticket;

  @Input('editable')
  public editable: boolean;

  private _maxAttachmentSize = 0;
  public groups: IGroupModel[] = [];
  public isChanged: boolean = false;
  public get isGroupChange() {
    return this.groups && this.groups.length > 0 && this.ticket && this.ticket.groupId != this.groups[0].groupId;
  }
  // Define the set of supported ticket activities
  private ticketActivityList: hd.IDropdownList = {
    Note: { id: 'note', caption: this.xsi18n.transform('note_Caption'), glyphicon: 'hd-icon hd-icon-note' },
    Attachment: {
      id: 'attachment',
      caption: this.xsi18n.transform('attachment_NewAttachment_Caption'),
      glyphicon: 'hd-icon hd-icon-attachment',
    },
  };
  // Define the group selection dropdown list dictionary with the default caption item
  public groupsDropdownList: hd.IDropdownList = {};
  // Whether a group has NOT been selected
  public invalidGroupSelected = false;
  // Whether the current device is a touch device, used to toggle use of ellipsis directive in group-selection dropdown
  public isTouchDevice: boolean;
  // The currently selected group from the groupsDropdownList
  public selectedGroupOption: string;
  public groupName: string;
  /** Determines if the currently logged-in user is licensed */
  public isLicensed: boolean = false;

  /** Constructor */
  constructor(
    // private errorHandlerService: ErrorHandlerService,
    private xsi18n: XsI18nPipe,
    private ticketService: TicketService,
    public ticketContextService: TicketContextService,
    private attachmentService: AttachmentService,
    private groupContext: GroupContextService,
    private globalEventBusService: GlobalEventBusService
  ) {
    this.isTouchDevice =
      navigator.maxTouchPoints || 'ontouchstart' in document.documentElement || 'ontouchstart' in window ? true : false;

    this.isLicensed = !this.ticketContextService.isLicenceExpired;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.ticketContextService.ticket) {
      this.groupName = this.groupContext.getGroupName(this.ticketContextService.ticket.groupId);
    }
  }

  ngOnInit(): void {
    this.groupContext
      .getAllowedGroups()
      .then((result: IGroupModel[]) => {
        this.groups = result;
        if (this.groups) {
          this.ticket.groupId = this.groups[0].groupId;
        }

        if (!this.groups || this.groups.length === 0) {
          return;
        }

        const defaultGroup = result.find(group => group.groupId == 1);
        this.ticket.groupId = !defaultGroup ? this.groups[0].groupId : 1;
      })
      .catch(error => {
        console.error('Failed to get Groups Information');
        console.error(error);
      });

    this.groupName = this.ticket.groupName;

    this.attachmentService.getConfig().subscribe((result: IAttachmentServiceConfig) => {
      this._maxAttachmentSize = result.maxFileSize;
    });

    this.globalEventBusService.register(GlobalEvents.validateTicket, () => {
      this.setDirty();
    });
  }

  getMaxAttachmentSize = (): number => this._maxAttachmentSize;

  /** Determines if the associated ticket is new */
  public isNew = () => {
    if (this.ticketContextService.ticket) {
      return this.ticketContextService.ticket.ticketId ? false : true;
    }
    return true;
  };

  public setDirty() {
    this.isChanged = true;
  }

  public get title() {
    return this.ticketContextService.ticket.title ? this.ticketContextService.ticket.title : '';
  }

  /** Called to update the ticket title */
  public set title(value: string) {
    this.ticketContextService.ticket.title = value;
  }

  public isValid() {
    return this.ticket && this.ticket.title && this.ticket.title.length > 0;
  }

  /** Adds a child activity of the given type to the ticket activity */
  public addActivity = (activityTypeId: number, defaultParams: hd.IDictionary<any> = null) => {
    const newActivity = { activityTypeId, details: {} } as Activity;
    if (!this.ticketContextService.activities) {
      this.ticketContextService.activities = [];
    }

    if (defaultParams) {
      for (const key of Object.keys(defaultParams)) {
        newActivity[key] = defaultParams[key];
      }
    }
    this.ticketContextService.activities.push(newActivity);
  };

  public isNoteActivity(activity: Activity): activity is NoteActivity {
    return activity.activityTypeId === ActivityTypeEnum.note;
  }

  /** Adds a new attachment for the specified file object */
  public addAttachment = $event => {
    const files: FileList = $event.target.files;
    const file = files[0];
    $event.target.value = null;
    this.addAttachmentService(file);
  };

  /** Adds a new attachment for the specified file object */
  private addAttachmentService = (file: File) => {
    const defaultParams: hd.IDictionary<any> = {
      fileName: file.name,
      fileSize: file.size,
      description: '',
      $$file: file,
      $$isLocal: true, // addActivity currently only creates new activities
    };
    this.addActivity(2, defaultParams);
  };

  public getDropDownActivityItems = (): hd.IDropdownList => {
    return this.ticketActivityList;
  };

  /** Gets the ticket status, lazily fetching it once the ticket is available */
  public getTicketStatus = (): hd.IDropdownList | hd.IDropdownListItem => {
    if (this.ticketContextService.ticket && this.ticketContextService.ticket.status) {
      return this.ticketService.getTicketStatus(this.ticketContextService.ticket.status);
    }

    return null;
  };

  public showGroupsDropdown = (): boolean => {
    return this.groups && this.groups.length > 1;
  };
}
